import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import i18n from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils/uuid";
import { normalizeStoragePath } from "@/lib/utils/storage";
import { sanitizeFilenameForStorage } from "@/lib/utils/filename";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";
import { parseISODateOrDateTime, toISODateOnlyLocal } from "@/lib/date-utils";
import {
  MAX_DOCUMENT_FILE_SIZE_MB,
  isAcceptedDocumentFile,
  isFileWithinDocumentSizeLimit,
  normalizeDocumentTypeName,
} from "@/lib/pet-documents/document-upload";

type AttachmentRow = Database["public"]["Tables"]["pet_attachments"]["Row"];
type AttachmentAnnotations = Database["public"]["Tables"]["pet_attachments"]["Update"]["annotations"];

export type DocumentAttachmentUpload = {
  file: File;
  name?: string;
  type: string;
  category?: string;
  description?: string | null;
  documentDate?: Date | string | null;
  examDate?: Date | string | null;
  tags?: string[];
};

export type ExamAttachmentUpload = DocumentAttachmentUpload;

const MAX_FILE_SIZE_MB = MAX_DOCUMENT_FILE_SIZE_MB;

const getUploadErrorMessage = (error: Error) => {
  const rawMessage = error.message ?? "";
  const normalizedMessage = rawMessage.toLowerCase();

  if (normalizedMessage.includes("maximum allowed size")) {
    return i18n.t("medicalRecords:attachments.fileSizeExceededStorage", {
      size: MAX_FILE_SIZE_MB,
    });
  }

  return rawMessage || i18n.t("medicalRecords:attachments.uploadError");
};

export const useExamAttachments = (petId?: string, medicalRecordId?: string, clinicId?: string | null) => {
  const queryClient = useQueryClient();
  const normalizeExamDate = (examDate?: Date | string | null): string | null => {
    if (!examDate) return null;
    if (typeof examDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(examDate)) return examDate;
    const parsed = typeof examDate === "string" ? parseISODateOrDateTime(examDate) : examDate;
    if (!parsed) return null;
    return toISODateOnlyLocal(parsed);
  };

  const {
    data: attachments = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["pet-attachments", petId, medicalRecordId],
    enabled: Boolean(petId),
    queryFn: async () => {
      if (!petId) return [] as AttachmentRow[];
      let query = supabase
        .from("pet_attachments")
        .select(`
          *,
          clinics (
            id,
            name
          )
        `)
        .eq("pet_id", petId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (medicalRecordId) {
        query = query.eq("medical_record_id", medicalRecordId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as AttachmentRow[];
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (items: DocumentAttachmentUpload[]) => {
      if (!petId || !clinicId) {
        throw new Error(i18n.t("medicalRecords:attachments.requiredContext"));
      }
      if (!items.length) return;

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error(i18n.t("medicalRecords:attachments.userNotAuthenticated"));

      const uploadedAttachments: AttachmentRow[] = [];

      for (const item of items) {
        if (!isFileWithinDocumentSizeLimit(item.file)) {
          throw new Error(i18n.t("medicalRecords:attachments.fileSizeExceededStorage", {
            size: MAX_FILE_SIZE_MB,
          }));
        }

        if (!isAcceptedDocumentFile(item.file)) {
          throw new Error(i18n.t("medicalRecords:attachments.fileTypeUnsupported"));
        }

        const documentType = normalizeDocumentTypeName(item.category ?? item.type);
        const documentName = (item.name ?? item.type ?? item.file.name).trim() || item.file.name;
        const occurredAt = item.documentDate ?? item.examDate;
        const parsedOccurredAt = typeof occurredAt === "string" ? parseISODateOrDateTime(occurredAt) : occurredAt;
        const safeOriginalName = sanitizeFilenameForStorage(item.file.name);
        const filePath = `pets/${petId}/${generateUUID()}-${safeOriginalName}`;
        const { error: uploadError } = await supabase.storage
          .from("pet-attachments")
          .upload(filePath, item.file, {
            contentType: item.file.type,
            upsert: false,
          });

        if (uploadError) throw uploadError;

        // Helper function to convert empty strings to null
        const normalizeId = (id?: string | null): string | null => {
          if (!id || id.trim() === "") return null;
          return id;
        };

        const { data: insertedAttachment, error: insertError } = await supabase
          .from("pet_attachments")
          .insert({
            pet_id: petId,
            clinic_id: clinicId,
            medical_record_id: normalizeId(medicalRecordId),
            created_by: userData.user.id,
            created_at: parsedOccurredAt ? parsedOccurredAt.toISOString() : undefined,
            type: documentType,
            category: documentType,
            file_path: filePath,
            filename: safeOriginalName,
            file_name: documentName,
            original_name: item.file.name,
            storage_bucket: "pet-attachments",
            file_size: item.file.size,
            mime_type: item.file.type,
            description: item.description ?? null,
            exam_date: normalizeExamDate(occurredAt),
            tags: item.tags ?? null,
            ocr_status: "idle",
          })
          .select()
          .single();

        if (insertError) throw insertError;
        if (insertedAttachment) {
          uploadedAttachments.push(insertedAttachment);


        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pet-attachments"] });
      queryClient.invalidateQueries({ queryKey: ["exam-results"] });
      invalidatePatientsCache(queryClient, { petIds: petId ? [petId] : null });
      toast.success(i18n.t("medicalRecords:attachments.uploadSuccess"));
    },
    onError: (err: Error) => {
      console.error("Erro ao enviar anexos:", err);
      toast.error(getUploadErrorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (attachment: AttachmentRow) => {
      const storagePath =
        normalizeStoragePath(attachment.file_path, attachment.storage_bucket) ??
        attachment.file_path;

      if (storagePath.startsWith("http")) {
        throw new Error("Caminho do arquivo inválido para remoção.");
      }
      const { error: storageError } = await supabase.storage
        .from(attachment.storage_bucket ?? "pet-attachments")
        .remove([storagePath]);
      if (storageError) throw storageError;
      const { error } = await supabase
        .from("pet_attachments")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", attachment.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pet-attachments"] });
      invalidatePatientsCache(queryClient, { petIds: petId ? [petId] : null });
      toast.success(i18n.t("medicalRecords:attachments.deleteSuccess"));
    },
    onError: (err: Error) => {
      console.error("Erro ao remover anexo:", err);
      toast.error("Não foi possível remover o anexo.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      attachmentId,
      name,
      type,
      description,
      examDate,
      occurredAt,
    }: {
      attachmentId: string;
      name?: string | null;
      type: string;
      description: string | null;
      examDate: string | null;
      occurredAt?: string | null;
    }) => {
      const payload: Database["public"]["Tables"]["pet_attachments"]["Update"] = {
        type: normalizeDocumentTypeName(type),
        category: normalizeDocumentTypeName(type),
        description,
        exam_date: normalizeExamDate(examDate),
      };
      if (name !== undefined) {
        payload.file_name = name?.trim() || null;
      }
      if (occurredAt) {
        payload.created_at = occurredAt;
      }
      const { error } = await supabase
        .from("pet_attachments")
        .update(payload)
        .eq("id", attachmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pet-attachments"] });
      invalidatePatientsCache(queryClient, { petIds: petId ? [petId] : null });
      toast.success(i18n.t("medicalRecords:attachments.updateSuccess"));
    },
    onError: (err: Error) => {
      console.error("Erro ao atualizar exame:", err);
      toast.error("Não foi possível atualizar o exame.");
    },
  });

  const annotationMutation = useMutation({
    mutationFn: async ({ attachmentId, text }: { attachmentId: string; text: string }) => {
      const { data, error } = await supabase
        .from("pet_attachments")
        .select("annotations")
        .eq("id", attachmentId)
        .single();
      if (error) throw error;
      const nextAnnotations = Array.isArray(data?.annotations) ? [...(data.annotations as unknown[])] : [];
      nextAnnotations.push({
        id: generateUUID(),
        text,
        created_at: new Date().toISOString(),
      });
      const annotationsPayload = nextAnnotations as unknown as AttachmentAnnotations;
      const { error: updateError } = await supabase
        .from("pet_attachments")
        .update({ annotations: annotationsPayload })
        .eq("id", attachmentId);
      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pet-attachments"] });
      invalidatePatientsCache(queryClient, { petIds: petId ? [petId] : null });
      toast.success("Anotação adicionada.");
    },
    onError: (err: Error) => {
      console.error("Erro ao salvar anotação:", err);
      toast.error("Não foi possível salvar a anotação.");
    },
  });

  return {
    attachments,
    isLoading,
    refetch,
    uploadAttachments: uploadMutation.mutateAsync,
    deleteAttachment: deleteMutation.mutateAsync,
    updateAttachment: updateMutation.mutateAsync,
    addAnnotation: annotationMutation.mutateAsync,
  };
};
